import { Component, TemplateRef, ViewChild } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Helper } from "src/app/core/helpers/helper";
import { UsersService } from "../../services/users.service";
import { OtpModalComponent } from "../otp-modal/otp-modal.component";
import { ToasterService } from "../../services/toaster.service";

@Component({
  selector: "app-edit-profile",
  templateUrl: "./edit-profile.component.html",
  styleUrl: "./edit-profile.component.scss",
  standalone: false
})
export class EditProfileComponent {
  isChangePassword!: boolean;
  editModalRef!: NgbModalRef;
  config: NgbModalOptions = { size: "md", centered: true, backdrop: "static" };

  editForm!: FormGroup;
  // otpProfile!: FormGroup;
  fieldTextType: boolean[] = [];

  profileDetails: any;

  updateType!: number;

  @ViewChild("template", { static: true }) template!: TemplateRef<any>;
  @ViewChild("otpModal", { static: true }) otpModal!: OtpModalComponent;
  @ViewChild("apiKeyGenerationConfirmationModal", { static: true }) apiKeyGenerationConfirmationModal!: TemplateRef<any>;
  secondModalRef!: NgbModalRef;

  constructor(public helper: Helper, private modalService: NgbModal, private userService: UsersService, private toasterService: ToasterService) {}

  /**
   * Opens the edit profile modal and fetches profile details to populate the form.
   * If profile details are available, patches the form with those details.
   *
   * @param details - The profile details to populate the form with.
   * @param isChangePassword - Boolean indicating whether to include password-related form controls.
   */
  modalOpen(details: any, isChangePassword: boolean = false): void {
    this.profileDetails = details;
    this.isChangePassword = isChangePassword;
    this.editModalRef = this.modalService.open(this.template, this.config);
    this._initForm();
    this.trimSpaces();

    if (this.profileDetails) {
      this._patchForm();
    }
  }

  /**
   * Initializes the edit form group with specific form controls based on the mode of operation.
   *
   * @remarks
   * The name field must contain alphabets only. Email is validated using a regex pattern from the helper class.
   */
  _initForm(): void {
    if (this.isChangePassword) {
      this.editForm = new FormGroup(
        {
          password: new FormControl("", Validators.required),
          newPassword: new FormControl("", [Validators.required, this.helper.passwordValidator(this.helper.adminSettings.passwordRule)]),
          confirmPassword: new FormControl("", Validators.required)
        },
        { validators: this.helper.passwordMatchValidator }
      );
    } else {
      this.editForm = new FormGroup({
        firstName: new FormControl("", [Validators.required, Validators.maxLength(this.helper.NameMaxLength), this.helper.nameValidator()]),
        lastName: new FormControl("", [Validators.required, Validators.maxLength(this.helper.NameMaxLength), this.helper.nameValidator()]),
        email: new FormControl("", [Validators.required, Validators.pattern(this.helper.emailRegex)]),
        countryPhoneCode: new FormControl("", Validators.required),
        phone: new FormControl("", Validators.required),
        apiKey: new FormControl(""),
        webhookUrl: new FormControl("")
      });
    }
  }

  /**
   * Patches the corporate details into the edit form.
   *
   * @remarks
   * This function is used to populate the form with existing profile details.
   */
  _patchForm(): void {
    this.editForm.patchValue({
      firstName: this.profileDetails.firstName,
      lastName: this.profileDetails.lastName,
      email: this.profileDetails.email,
      phone: this.profileDetails.phone,
      countryPhoneCode: this.profileDetails.countryPhoneCode,
      apiKey: this.profileDetails.apiKey,
      webhookUrl: this.profileDetails.webhookUrl
    });
  }

  /**
   * Toggles the visibility of the password field.
   *
   * @param type - The index of the password field to toggle.
   */
  toggleFieldTextType(type: number): void {
    this.fieldTextType[type] = !this.fieldTextType[type];
  }

  /**
   * Opens the OTP verification modal if email or phone number is changed.
   * If neither is changed, updates the profile directly.
   */
  openVerificationModal(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    if (this.isChangePassword) {
      this.updateProfile();
      return;
    }

    const params: any = {};
    let sendTo: any;
    let loginBy: any;

    const isEmailChanged = this.isEmailChanged();
    const isPhoneChanged = this.isPhoneChanged();

    if (isEmailChanged) {
      this.updateType = this.helper.OTPSendMode.EMAIL;
      sendTo = this.updateType;
      loginBy = this.helper.LoginBy.EMAIL;
      params.email = this.editForm.value.email;
    }

    if (isPhoneChanged) {
      this.updateType = isEmailChanged ? this.helper.OTPSendMode.SMS_EMAIL : this.helper.OTPSendMode.SMS;
      sendTo = this.updateType;
      loginBy = isEmailChanged ? this.helper.OTPSendMode.SMS_EMAIL : this.helper.LoginBy.PHONE;
      params.countryPhoneCode = this.editForm.value.countryPhoneCode;
      params.phone = this.editForm.value.phone;
    }

    if (!isEmailChanged && !isPhoneChanged) {
      this.updateProfile();
      return;
    }

    this.helper.showBtnLoader[1] = true;

    this.userService.checkUserRegistered({ ...params, loginBy, isShowErrorToast: false, isShowSuccessToast: false }).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;

      if (!response) {
        this.otpModal.openModal({ ...params, sendTo }, false);
      } else {
        this.toasterService.showNotification("error", response.message);
      }
    });
  }

  generateApiKey(): void {
    const apiKey = this.helper.generateRandomString(32);
    this.profileDetails.apiKey = apiKey;
    this.editForm.get("apiKey")?.setValue(apiKey);
  }

  copyApiKeyToClipboard(): void {
    const apiKey = this.profileDetails?.apiKey ?? this.editForm.get("apiKey")?.value ?? "";
    if (apiKey !== "") {
      this.helper.copyTextToClipboard(apiKey);
    }
  }

  openApiKeyGenerationModal(): void {
    this.secondModalRef = this.modalService.open(this.apiKeyGenerationConfirmationModal, { size: "md", centered: true });
  }

  /**
   * Updates the profile details.
   *
   * @param otps - The OTPs for verification.
   */
  updateProfile(otps: any = {}): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const params = { ...this.editForm.value, sendTo: this.updateType, ...otps };

    this.userService.updateDispatcherDetails(params).subscribe((res) => {
      this.helper.showBtnLoader[1] = false;
      if (res) {
        this.closeModal();
        this.otpModal.closeOtpModal();
      }
    });
  }

  /**
   * Trims leading spaces from the first name and last name fields.
   */
  trimSpaces(): void {
    this.editForm.get("firstName")?.valueChanges.subscribe((value) => {
      if (value !== value.trimStart()) {
        this.editForm.get("firstName")?.setValue(value.trimStart(), { emitEvent: false });
      }
    });

    this.editForm.get("lastName")?.valueChanges.subscribe((value) => {
      if (value !== value.trimStart()) {
        this.editForm.get("lastName")?.setValue(value.trimStart(), { emitEvent: false });
      }
    });
  }

  /**
   * Detects if the email has been changed.
   *
   * @returns A boolean indicating if the email has been changed.
   */
  isEmailChanged(): boolean {
    const currentValue = this.editForm.value;
    const originalValue = this.profileDetails;

    return currentValue.email !== originalValue.email;
  }

  /**
   * Detects if the phone number has been changed.
   *
   * @returns A boolean indicating if the phone number has been changed.
   */
  isPhoneChanged(): boolean {
    const currentValue = this.editForm.value;
    const originalValue = this.profileDetails;

    return currentValue.phone.toString() !== originalValue.phone;
  }

  /**
   * Closes the edit profile modal.
   */
  closeModal(): void {
    this.editModalRef.close();
  }

  get nameInput(): AbstractControl<any, any> | null {
    return this.editForm.get("firstName");
  }

  get lastNameInput(): AbstractControl<any, any> | null {
    return this.editForm.get("lastName");
  }

  get emailInput(): AbstractControl<any, any> | null {
    return this.editForm.get("email");
  }

  get phoneInput(): AbstractControl<any, any> | null {
    return this.editForm.get("phone");
  }

  get passwordInput(): AbstractControl<any, any> | null {
    return this.editForm.get("password");
  }

  get newPasswordInput(): AbstractControl<any, any> | null {
    return this.editForm.get("newPassword");
  }

  get confirmPasswordInput(): AbstractControl<any, any> | null {
    return this.editForm.get("confirmPassword");
  }
}
